import 'draft-js/dist/Draft.css';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Alert, Button, Col, Divider, Row, Select } from 'antd';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { WARNING_ORANGE } from '../../../foundation/config/colors';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { VariantTranscript } from '../redux';
import { useVariantUpdatePrimaryTranscriptModalLogic } from './hooks';

type VariantLinkModalType = {
  closeModal: () => void;
  variantLink: string;
  handleSelectTranscriptClick?: () => void;
  selectedPrimaryVariantTranscriptData: VariantTranscript | null;
  setNewPrimaryTranscript?: React.Dispatch<
    React.SetStateAction<VariantTranscript | null>
  >;
};

const VariantUpdatePrimaryTranscriptModal = ({
  closeModal,
  variantLink,
  handleSelectTranscriptClick,
  selectedPrimaryVariantTranscriptData,
  setNewPrimaryTranscript,
}: VariantLinkModalType) => {
  const { isReadOnly, canEditWorkflow } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    isLoading,
    variant,
    commentEditorState,
    selectedPrimaryVariantTranscriptDataKeyValues,
    variantKeyValues,
    variantTranscriptLists,
    createModalOpts,
    handleEditorChange,
    handleTranscriptChange,
    handleSaveChanges,
  } = useVariantUpdatePrimaryTranscriptModalLogic({
    closeModal,
    variantLink,
    selectedPrimaryVariantTranscriptData,
    setNewPrimaryTranscript,
  });

  // Build modal title JSX here since hooks can't return JSX
  const modalOpts = createModalOpts(
    <span className="h-variant-link-modal__modal-title">
      Variant:{' '}
      <span style={{ textDecoration: 'underline' }}>{variantLink}</span>
    </span>,
  );

  const variantValueRenderer = useCallback(
    (label: string, value?: string) => {
      let shouldHighlight = false;

      if (
        selectedPrimaryVariantTranscriptDataKeyValues &&
        selectedPrimaryVariantTranscriptData
      ) {
        const normalizedLabel = label?.toLowerCase();

        for (const i of selectedPrimaryVariantTranscriptDataKeyValues) {
          const normalizedKey = i.label?.toLowerCase();
          if (normalizedKey === normalizedLabel) {
            if (i.value !== value) {
              shouldHighlight = true;
              value = i.value;
              break;
            }
          }
        }
      }

      return (
        <>
          <Row>
            <Col flex={'35%'} className="variant-summary__field-label">
              {label}
            </Col>
            <Col
              flex={'65%'}
              className="variant-summary__field-value"
              style={{
                color: shouldHighlight ? WARNING_ORANGE : 'black',
              }}
            >
              {value}
            </Col>
          </Row>
          <Divider style={{ margin: '5px 0px' }} />
        </>
      );
    },
    [
      selectedPrimaryVariantTranscriptDataKeyValues,
      selectedPrimaryVariantTranscriptData,
    ],
  );

  const renderVariantInformation = useCallback(() => {
    return variantKeyValues.map((item) => {
      if (item.label === 'Transcript') {
        const currentTranscriptExternalId =
          selectedPrimaryVariantTranscriptDataKeyValues &&
          selectedPrimaryVariantTranscriptDataKeyValues.find(
            (i) => i.label === item.label,
          )?.value;

        return (
          <>
            <Row align={'bottom'}>
              <Col flex={'35%'} className="variant-summary__field-label">
                {item.label}
              </Col>
              <Col flex={'65%'} className="variant-summary__field-value">
                <Select
                  value={
                    currentTranscriptExternalId
                      ? currentTranscriptExternalId
                      : variant?.transcript_external_id
                  }
                  className={`variant-summary__select-field
    ${currentTranscriptExternalId ? ' variant-summary__select-field--highlight' : ''}`}
                  options={variantTranscriptLists}
                  onChange={handleTranscriptChange}
                  disabled={!canEditWorkflow}
                />
              </Col>
            </Row>
            <Divider style={{ margin: '5px 0px' }} />
          </>
        );
      } else {
        return variantValueRenderer(item.label, item.value);
      }
    });
  }, [
    variantKeyValues,
    selectedPrimaryVariantTranscriptDataKeyValues,
    selectedPrimaryVariantTranscriptData,
    variant,
    variantTranscriptLists,
    setNewPrimaryTranscript,
    variantValueRenderer,
  ]);

  return (
    <Modal {...modalOpts} className="h-variant-link-modal">
      {isLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <div className="h-variant-link-modal__content-wrapper">
        <div className="h-variant-link-modal__info-wrapper">
          {renderVariantInformation()}
        </div>
        {
          <div className="h-variant-link-modal__select-tran-wrapper">
            <Button
              icon={<FontAwesomeIcon icon={faPlus} />}
              className="h-variant-link-modal__select-tran-btn"
              onClick={handleSelectTranscriptClick}
              disabled={!canEditWorkflow}
            >
              Select transcript
            </Button>
          </div>
        }
      </div>
      {
        <>
          <RichTextEditor
            name="comment"
            editorState={commentEditorState}
            onChange={(newState) => handleEditorChange(newState)}
            placeholder=""
            label=""
            readOnly={!canEditWorkflow}
          />
        </>
      }
      <div className="h-variant-link-modal__save-changes-btn-wrapper">
        <Button
          type="primary"
          onClick={handleSaveChanges}
          className="h-variant-link-modal__save-changes-btn"
          disabled={!canEditWorkflow}
        >
          Save changes
        </Button>
      </div>
    </Modal>
  );
};

export default VariantUpdatePrimaryTranscriptModal;
